import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";

import logo from "assets/images/logo.svg";
import leafFlutteringInWind from "assets/images/leaf-fluttering-in-wind.png";
import theBasicBreather from "assets/images/the-basic-breather.png";
import theBasicBreatherWebp from "assets/images/the-basic-breather.png?as=webp";
import sneezingFace from "assets/images/sneezing-face.png";
import theBigSneeze from "assets/images/the-big-sneeze.jpg";
import theBigSneezeWebp from "assets/images/the-big-sneeze.jpg?as=webp";
import baby from "assets/images/baby.png";
import theRookieParent from "assets/images/the-rookie-parent.png";
import theRookieParentWebp from "assets/images/the-rookie-parent.png?as=webp";
import dogFace from "assets/images/dog-face.png";
import theCritterCuddler from "assets/images/the-critter-cuddler.jpg";
import theCritterCuddlerWebp from "assets/images/the-critter-cuddler.jpg?as=webp";
import construction from "assets/images/construction.png";
import theHomeWrecker from "assets/images/the-home-wrecker.jpg";
import theHomeWreckerWebp from "assets/images/the-home-wrecker.jpg?as=webp";
import raisingHands from "assets/images/raising-hands.png";
import woman from "assets/images/woman.png";
import theMamaToBe from "assets/images/the-mama-to-be.jpg";
import theMamaToBeWebp from "assets/images/the-mama-to-be.jpg?as=webp";
import explodingHead from "assets/images/exploding-head.png";
import theOverreactor from "assets/images/the-overreactor.jpg";
import theOverreactorWebp from "assets/images/the-overreactor.jpg?as=webp";
import flexedBiceps from "assets/images/flexed-biceps.png";
import LazyLoad from "react-lazyload";
import { useSelector } from "react-redux";
import LazyImg from "../common/LazyImg";
import FadeIn from "../common/FadeIn";
import {
  CURRENCY_MAPPING,
  FILTER_NAMES,
  PRODUCT_VARIANT_ABBREVIATIONS,
  SUPPORTED_REGIONS,
} from "../../../shared/enums";
import { selectLocalizationData } from "../../features/localization/localizationSlice";
import {
  selectRegionalPricesData,
  selectRegionalPricesError,
} from "../../features/regionalPrices/regionalPricesSlice";
import { getFilterPrice } from "../../features/regionalPrices/regionalPricesHelpers";
import generateKey from "../../../shared/utils/generateKey";

/**
 * @param index
 * @param setScrollToRefs
 * @param formatter
 * @param bb
 * @param localization
 * @returns {JSX.Element}
 * @constructor
 */
const BasicBreatherComponent = ({
  index,
  setScrollToRefs,
  formatter,
  regionalPrice,
  localization,
}) => (
  <section
    id="basic-breather"
    className="filter-block"
    ref={(el) => setScrollToRefs(index, el)}
  >
    <div className="container">
      <div className="filter-block__primary">
        <figure className="filter-block__image">
          <LazyLoad>
            <FadeIn>
              <picture>
                <source srcSet={theBasicBreatherWebp} type="image/webp" />
                <source srcSet={theBasicBreather} type="image/png" />
                <img src={theBasicBreather} alt="the-basic-breather" />
              </picture>
            </FadeIn>
          </LazyLoad>
        </figure>
        <div className="filter-block__content">
          <h2 className="filter-block__title" style={{ color: "#3E4649" }}>
            The{" "}
            <LazyImg src={leafFlutteringInWind} alt="leaf-fluttering-in-wind" />{" "}
            <br />
            Basic Breather
          </h2>
          <figure className="filter-block__image">
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={theBasicBreatherWebp} type="image/webp" />
                  <source srcSet={theBasicBreather} type="image/png" />
                  <img src={theBasicBreather} alt="the-basic-breather" />
                </picture>
              </FadeIn>
            </LazyLoad>
          </figure>
          <h3 className="filter-block__subtitle">
            For the everyday lover of clean air.
          </h3>
          <div className="filter-block__description">
            <p>
              This brilliant basic is designed for those who don’t have any
              specific needs, other than the desire for clean, fresh air.
            </p>
          </div>
        </div>
        <div className="filter-block__price">
          <div className="filter-block__price-info">
            <p>
              <strong>{formatter.format(regionalPrice.price)}</strong>
              {localization.region !== SUPPORTED_REGIONS.SG && (
                <span key={generateKey("filter-block__free-info")}>
                  {" "}
                  or{" "}
                  <span
                    className="filter-block__free-sign"
                    style={{ color: " #3E4649" }}
                  >
                    FREE
                  </span>{" "}
                  with Auto-Refill with the purchase of a Mila unit
                </span>
              )}
            </p>
          </div>
          <div className="filter-block__button-wrap">
            <Link
              to="/filters/basic-breather"
              className="btn"
              style={{
                borderColor: "#3E4649",
                background: "#3E4649",
                color: "#ffffff",
              }}
            >
              Learn more
            </Link>
            <a
              href="https://buy.milacares.com/collections/mila-filter"
              className="btn btn--outline"
              style={{
                border: "2px solid #3E4649",
                color: "#3E4649",
              }}
            >
              Buy a standalone filter
            </a>
          </div>
        </div>
      </div>
      <ul className="filter-block__secondary icon-list">
        <li className="icon-list__item">
          <svg
            className="icon icon-ideal-room-size"
            style={{ color: "#3E4649" }}
          >
            <use xlinkHref="#icon-ideal-room-size" />
          </svg>
          <h4 className="icon-list__title">Ideal Room Size</h4>
          <p>Does its best work in larger rooms, like your main living area.</p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-hepa-filter" style={{ color: "#3E4649" }}>
            <use xlinkHref="#icon-hepa-filter" />
          </svg>
          <h4 className="icon-list__title">HEPA Filter </h4>
          <p>
            H12 HEPA filter optimized for maximum particle filtration
            (pollution, dust, smoke) to keep lungs clear.
          </p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-carbon-filter" style={{ color: "#3E4649" }}>
            <use xlinkHref="#icon-carbon-filter" />
          </svg>
          <h4 className="icon-list__title">Carbon Filter</h4>
          <p>
            No carbon in this one, so if you're looking for odor and VOC
            filtration, we recommend one of the five filters with carbon.
          </p>
        </li>
      </ul>
    </div>
  </section>
);

/**
 * @param index
 * @param setScrollToRefs
 * @param formatter
 * @param regionalPrice
 * @param localization
 * @returns {JSX.Element}
 * @constructor
 */
const BigSneezeComponent = ({
  index,
  setScrollToRefs,
  formatter,
  regionalPrice,
  localization,
}) => (
  <section
    id="big-sneeze"
    className="filter-block"
    ref={(el) => setScrollToRefs(index, el)}
  >
    <div className="container">
      <div className="filter-block__primary">
        <figure className="filter-block__image">
          <LazyLoad>
            <FadeIn>
              <picture>
                <source srcSet={theBigSneezeWebp} type="image/webp" />
                <source srcSet={theBigSneeze} type="image/jpg" />
                <img src={theBigSneeze} alt="the-big-sneeze" />
              </picture>
            </FadeIn>
          </LazyLoad>
        </figure>
        <div className="filter-block__content">
          <h2 className="filter-block__title" style={{ color: "#FFCA0C" }}>
            The <LazyImg src={sneezingFace} alt="sneezing-face" /> <br />
            Big Sneeze
          </h2>
          <h3 className="filter-block__subtitle">
            Helping you fight back against allergens.
          </h3>
          <figure className="filter-block__image">
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={theBigSneezeWebp} type="image/webp" />
                  <source srcSet={theBigSneeze} type="image/jpg" />
                  <img src={theBigSneeze} alt="the-big-sneeze" />
                </picture>
              </FadeIn>
            </LazyLoad>
          </figure>
          <div className="filter-block__description">
            <p>
              Consider yourself saved from indoor/outdoor allergies with this
              filter designed specifically to eliminate the particles that make
              you a-a-achoo.
            </p>
          </div>
        </div>
        <div className="filter-block__price">
          <div className="filter-block__price-info">
            <p>
              <strong>{formatter.format(regionalPrice.price)}</strong>
              {localization.region !== SUPPORTED_REGIONS.SG && (
                <span key={generateKey("filter-block__free-info")}>
                  {" "}
                  or{" "}
                  <span
                    className="filter-block__free-sign"
                    style={{ color: " #FFCA0C" }}
                  >
                    FREE
                  </span>{" "}
                  with Auto-Refill with the purchase of a Mila unit{" "}
                </span>
              )}
            </p>
          </div>
          <div className="filter-block__button-wrap">
            <Link
              to="/filters/big-sneeze"
              className="btn"
              style={{
                borderColor: "#FFCA0C",
                background: "#FFCA0C",
                color: "#ffffff",
              }}
            >
              Learn more
            </Link>
            <a
              href="https://buy.milacares.com/collections/mila-filter "
              className="btn btn--outline"
              style={{
                border: "2px solid #FFCA0C",
                color: "#FFCA0C",
              }}
            >
              Buy a standalone filter
            </a>
          </div>
        </div>
      </div>
      <ul className="filter-block__secondary icon-list">
        <li className="icon-list__item">
          <svg
            className="icon icon-ideal-room-size"
            style={{ color: "#FFCA0C" }}
          >
            <use xlinkHref="#icon-ideal-room-size" />
          </svg>
          <h4 className="icon-list__title">Ideal Room Size</h4>
          <p>
            Does its thing best in smaller living rooms and larger bedrooms.
          </p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-hepa-filter" style={{ color: "#FFCA0C" }}>
            <use xlinkHref="#icon-hepa-filter" />
          </svg>
          <h4 className="icon-list__title">HEPA Filter </h4>
          <p>
            H13 HEPA filter optimized for sneeze triggers like pollen, dander,
            and dust allergies.
          </p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-carbon-filter" style={{ color: "#FFCA0C" }}>
            <use xlinkHref="#icon-carbon-filter" />
          </svg>
          <h4 className="icon-list__title">Carbon Filter</h4>
          <p>
            No carbon in this one, so if you're looking for odor and VOC
            filtration, we recommend one of the five filters with carbon.
          </p>
        </li>
      </ul>
    </div>
  </section>
);

/**
 * @param index
 * @param setScrollToRefs
 * @param formatter
 * @param regionalPrice
 * @param localization
 * @returns {JSX.Element}
 * @constructor
 */
const RookieParentComponent = ({
  index,
  setScrollToRefs,
  formatter,
  regionalPrice,
  localization,
}) => (
  <section
    id="rookie-parent"
    className="filter-block"
    ref={(el) => setScrollToRefs(index, el)}
  >
    <div className="container">
      <div className="filter-block__primary">
        <figure className="filter-block__image">
          <LazyLoad>
            <FadeIn>
              <picture>
                <source srcSet={theRookieParentWebp} type="image/webp" />
                <source srcSet={theRookieParent} type="image/png" />
                <img src={theRookieParent} alt="The-Rookie-Parent" />
              </picture>
            </FadeIn>
          </LazyLoad>
        </figure>
        <div className="filter-block__content">
          <h2 className="filter-block__title" style={{ color: "#8852F6" }}>
            The <LazyImg src={baby} alt="baby" /> <br />
            Rookie Parent
          </h2>
          <h3 className="filter-block__subtitle">
            Making the air clean and safe for your littles.
          </h3>
          <figure className="filter-block__image">
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={theRookieParentWebp} type="image/webp" />
                  <source srcSet={theRookieParent} type="image/png" />
                  <img src={theRookieParent} alt="The-Rookie-Parent" />
                </picture>
              </FadeIn>
            </LazyLoad>
          </figure>
          <div className="filter-block__description">
            <p>
              This filter is the perfect protection for young families with
              sensitive or developing immune systems (and stinky diapers).
            </p>
          </div>
        </div>
        <div className="filter-block__price">
          <div className="filter-block__price-info">
            <p>
              <strong>{formatter.format(regionalPrice.price)}</strong>
              {localization.region !== SUPPORTED_REGIONS.SG && (
                <span key={generateKey("filter-block__free-info")}>
                  {" "}
                  or{" "}
                  <span
                    className="filter-block__free-sign"
                    style={{ color: " #8852F6" }}
                  >
                    FREE
                  </span>{" "}
                  with Auto-Refill with the purchase of a Mila unit{" "}
                </span>
              )}
            </p>
          </div>
          <div className="filter-block__button-wrap">
            <Link
              to="/filters/rookie-parent"
              className="btn"
              style={{
                borderColor: "#8852F6",
                background: "#8852F6",
                color: "#ffffff",
              }}
            >
              Learn more
            </Link>
            <a
              href="https://buy.milacares.com/collections/mila-filter "
              className="btn btn--outline"
              style={{
                border: "2px solid #8852F6",
                color: "#8852F6",
              }}
            >
              Buy a standalone filter
            </a>
          </div>
        </div>
      </div>
      <ul className="filter-block__secondary icon-list">
        <li className="icon-list__item">
          <svg
            className="icon icon-ideal-room-size"
            style={{ color: "#8852F6" }}
          >
            <use xlinkHref="#icon-ideal-room-size" />
          </svg>
          <h4 className="icon-list__title">Ideal Room Size</h4>
          <p>
            Works its best magic in bedrooms, nurseries, and smaller living
            rooms.
          </p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-hepa-filter" style={{ color: "#8852F6" }}>
            <use xlinkHref="#icon-hepa-filter" />
          </svg>
          <h4 className="icon-list__title">HEPA Filter </h4>
          <p>
            H12 HEPA filter designed to fight off particles like dust,
            pollution, and smoke.
          </p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-carbon-filter" style={{ color: "#8852F6" }}>
            <use xlinkHref="#icon-carbon-filter" />
          </svg>
          <h4 className="icon-list__title">Carbon Filter</h4>
          <p>
            0.70 lbs of pure granular carbon to capture VOCs, odors, and gases.
          </p>
        </li>
      </ul>
    </div>
  </section>
);

/**
 * @param index
 * @param setScrollToRefs
 * @param formatter
 * @param regionalPrice
 * @param localization
 * @returns {JSX.Element}
 * @constructor
 */
const CritterCuddlerComponent = ({
  index,
  setScrollToRefs,
  formatter,
  regionalPrice,
  localization,
}) => (
  <section
    id="critter-cuddler"
    className="filter-block"
    ref={(el) => setScrollToRefs(index, el)}
  >
    <div className="container">
      <div className="filter-block__primary">
        <figure className="filter-block__image">
          <LazyLoad>
            <FadeIn>
              <picture>
                <source srcSet={theCritterCuddlerWebp} type="image/webp" />
                <source srcSet={theCritterCuddler} type="image/jpg" />
                <img src={theCritterCuddler} alt="the-critter-cuddler" />
              </picture>
            </FadeIn>
          </LazyLoad>
        </figure>
        <div className="filter-block__content">
          <h2 className="filter-block__title" style={{ color: "#F42D53" }}>
            The <LazyImg src={dogFace} alt="dog-face" /> <br />
            Critter Cuddler
          </h2>
          <h3 className="filter-block__subtitle">
            Clearing the air for fur families.
          </h3>
          <figure className="filter-block__image">
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={theCritterCuddlerWebp} type="image/webp" />
                  <source srcSet={theCritterCuddler} type="image/jpg" />
                  <img src={theCritterCuddler} alt="the-critter-cuddler" />
                </picture>
              </FadeIn>
            </LazyLoad>
          </figure>
          <div className="filter-block__description">
            <p>
              This HEPA + heavy-duty carbon filter is specially designed to keep
              animal funk and particles out of your air.
            </p>
          </div>
        </div>
        <div className="filter-block__price">
          <div className="filter-block__price-info">
            <p>
              <strong>{formatter.format(regionalPrice.price)}</strong>
              {localization.region !== SUPPORTED_REGIONS.SG && (
                <span key={generateKey("filter-block__free-info")}>
                  {" "}
                  or{" "}
                  <span
                    className="filter-block__free-sign"
                    style={{ color: " #F42D53" }}
                  >
                    FREE
                  </span>{" "}
                  with Auto-Refill with the purchase of a Mila unit{" "}
                </span>
              )}
            </p>
          </div>
          <div className="filter-block__button-wrap">
            <Link
              to="/filters/critter-cuddler"
              className="btn"
              style={{
                borderColor: "#F42D53",
                background: "#F42D53",
                color: "#ffffff",
              }}
            >
              Learn more
            </Link>
            <a
              href="https://buy.milacares.com/collections/mila-filter "
              className="btn btn--outline"
              style={{
                border: "2px solid #F42D53",
                color: "#F42D53",
              }}
            >
              Buy a standalone filter
            </a>
          </div>
        </div>
      </div>
      <ul className="filter-block__secondary icon-list">
        <li className="icon-list__item">
          <svg
            className="icon icon-ideal-room-size"
            style={{ color: "#F42D53" }}
          >
            <use xlinkHref="#icon-ideal-room-size" />
          </svg>
          <h4 className="icon-list__title">Ideal Room Size</h4>
          <p>
            Works its best magic in smaller living rooms and larger bedrooms.
          </p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-hepa-filter" style={{ color: "#F42D53" }}>
            <use xlinkHref="#icon-hepa-filter" />
          </svg>
          <h4 className="icon-list__title">HEPA Filter </h4>
          <p>
            H13 HEPA filter optimized for pollen, dander, and dust allergens
            from furry friends.
          </p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-carbon-filter" style={{ color: "#F42D53" }}>
            <use xlinkHref="#icon-carbon-filter" />
          </svg>
          <h4 className="icon-list__title">Carbon Filter</h4>
          <p>
            0.84 lbs of modified granular carbon specifically targeted for
            ammonia removal.
          </p>
        </li>
        <li className="icon-list__item">
          <svg
            className="icon icon-filter-coating"
            style={{ color: "#F42D53" }}
          >
            <use xlinkHref="#icon-filter-coating" />
          </svg>
          <h4 className="icon-list__title">Mila Sock</h4>
          <p>Comes with a washable Mila Sock for larger particles and fur.</p>
        </li>
      </ul>
    </div>
  </section>
);

/**
 * @param index
 * @param setScrollToRefs
 * @param formatter
 * @param regionalPrice
 * @param localization
 * @returns {JSX.Element}
 * @constructor
 */
const HomeWreckerComponent = ({
  index,
  setScrollToRefs,
  formatter,
  regionalPrice,
  localization,
}) => (
  <section
    id="home-wrecker"
    className="filter-block"
    ref={(el) => setScrollToRefs(index, el)}
  >
    <div className="container">
      <div className="filter-block__primary">
        <figure className="filter-block__image">
          <LazyLoad>
            <FadeIn>
              <picture>
                <source srcSet={theHomeWreckerWebp} type="image/webp" />
                <source srcSet={theHomeWrecker} type="image/jpg" />
                <img src={theHomeWrecker} alt="the-home-wrecker" />
              </picture>
            </FadeIn>
          </LazyLoad>
        </figure>
        <div className="filter-block__content">
          <h2 className="filter-block__title" style={{ color: "#FF810D" }}>
            The <LazyImg src={construction} alt="construction" /> <br />
            Home Wrecker
          </h2>
          <h3 className="filter-block__subtitle">
            Protecting you from that “new home” smell.
          </h3>
          <figure className="filter-block__image">
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={theHomeWreckerWebp} type="image/webp" />
                  <source srcSet={theHomeWrecker} type="image/jpg" />
                  <img src={theHomeWrecker} alt="the-home-wrecker" />
                </picture>
              </FadeIn>
            </LazyLoad>
          </figure>
          <div className="filter-block__description">
            <p>
              Taking a paintbrush (or sledgehammer) to your place? This filter
              makes sure the chemicals and odors from new paint, carpet, or
              furniture don’t linger.
            </p>
          </div>
        </div>
        <div className="filter-block__price">
          <div className="filter-block__price-info">
            <p>
              <strong>{formatter.format(regionalPrice.price)}</strong>
              {localization.region !== SUPPORTED_REGIONS.SG && (
                <span key={generateKey("filter-block__free-info")}>
                  {" "}
                  or{" "}
                  <span
                    className="filter-block__free-sign"
                    style={{ color: " #FF810D" }}
                  >
                    FREE
                  </span>{" "}
                  with Auto-Refill with the purchase of a Mila unit{" "}
                </span>
              )}
            </p>
          </div>
          <div className="filter-block__button-wrap">
            <Link
              to="/filters/home-wrecker"
              className="btn"
              style={{
                borderColor: "#FF810D",
                background: "#FF810D",
                color: "#ffffff",
              }}
            >
              Learn more
            </Link>
            <a
              href="https://buy.milacares.com/collections/mila-filter "
              className="btn btn--outline"
              style={{
                border: "2px solid #FF810D",
                color: "#FF810D",
              }}
            >
              Buy a standalone filter
            </a>
          </div>
        </div>
      </div>
      <ul className="filter-block__secondary icon-list">
        <li className="icon-list__item">
          <svg
            className="icon icon-ideal-room-size"
            style={{ color: "#FF810D" }}
          >
            <use xlinkHref="#icon-ideal-room-size" />
          </svg>
          <h4 className="icon-list__title">Ideal Room Size</h4>
          <p>Works best in standard size living rooms and bedrooms.</p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-hepa-filter" style={{ color: "#FF810D" }}>
            <use xlinkHref="#icon-hepa-filter" />
          </svg>
          <h4 className="icon-list__title">HEPA Filter </h4>
          <p>
            H12 HEPA filter designed to fight off particles like dust,
            pollution, and smoke.
          </p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-carbon-filter" style={{ color: "#FF810D" }}>
            <use xlinkHref="#icon-carbon-filter" />
          </svg>
          <h4 className="icon-list__title">Carbon Filter</h4>
          <p>
            Dual layer of 1.96 lbs of modified granular carbon specifically
            targeting formaldehyde and odors from out-gassing flooring,
            furniture, and paint.
            <LazyImg src={raisingHands} alt="raising-hands" />
          </p>
        </li>
      </ul>
    </div>
  </section>
);

/**
 * @param index
 * @param setScrollToRefs
 * @param formatter
 * @param regionalPrice
 * @param localization
 * @returns {JSX.Element}
 * @constructor
 */
const MamaToBeComponent = ({
  index,
  setScrollToRefs,
  formatter,
  regionalPrice,
  localization,
}) => (
  <section
    id="mama-to-be"
    className="filter-block"
    ref={(el) => setScrollToRefs(index, el)}
  >
    <div className="container">
      <div className="filter-block__primary">
        <figure className="filter-block__image">
          <LazyLoad>
            <FadeIn>
              <picture>
                <source srcSet={theMamaToBeWebp} type="image/webp" />
                <source srcSet={theMamaToBe} type="image/jpg" />
                <img src={theMamaToBe} alt="the-mama-to-be" />
              </picture>
            </FadeIn>
          </LazyLoad>
        </figure>
        <div className="filter-block__content">
          <h2 className="filter-block__title" style={{ color: "#1CE4D5" }}>
            The <LazyImg src={woman} alt="woman" /> <br />
            Mama-to-Be
          </h2>
          <h3 className="filter-block__subtitle">
            Extra protection when you’re breathing for two.
          </h3>
          <figure className="filter-block__image">
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={theMamaToBeWebp} type="image/webp" />
                  <source srcSet={theMamaToBe} type="image/jpg" />
                  <img src={theMamaToBe} alt="the-mama-to-be" />
                </picture>
              </FadeIn>
            </LazyLoad>
          </figure>
          <div className="filter-block__description">
            <p>
              H14 HEPA provides the ultimate protection (plus elimination of all
              the odors you can’t stand the smell of right now).
            </p>
          </div>
        </div>
        <div className="filter-block__price">
          <div className="filter-block__price-info">
            <p>
              <strong>{formatter.format(regionalPrice.price)}</strong>
              {localization.region !== SUPPORTED_REGIONS.SG && (
                <span key={generateKey("filter-block__free-info")}>
                  {" "}
                  or{" "}
                  <span
                    className="filter-block__free-sign"
                    style={{ color: " #1CE4D5" }}
                  >
                    FREE
                  </span>{" "}
                  with Auto-Refill with the purchase of a Mila unit{" "}
                </span>
              )}
            </p>
          </div>
          <div className="filter-block__button-wrap">
            <Link
              to="/filters/mama-to-be"
              className="btn"
              style={{
                borderColor: "#1CE4D5",
                background: "#1CE4D5",
                color: "#ffffff",
              }}
            >
              Learn more
            </Link>
            <a
              href="https://buy.milacares.com/collections/mila-filter "
              className="btn btn--outline"
              style={{
                border: "2px solid #1CE4D5",
                color: "#1CE4D5",
              }}
            >
              Buy a standalone filter
            </a>
          </div>
        </div>
      </div>
      <ul className="filter-block__secondary icon-list">
        <li className="icon-list__item">
          <svg
            className="icon icon-ideal-room-size"
            style={{ color: "#1CE4D5" }}
          >
            <use xlinkHref="#icon-ideal-room-size" />
          </svg>
          <h4 className="icon-list__title">Ideal Room Size</h4>
          <p>Works best in the bedroom (run it while you snooze!).</p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-hepa-filter" style={{ color: "#1CE4D5" }}>
            <use xlinkHref="#icon-hepa-filter" />
          </svg>
          <h4 className="icon-list__title">HEPA Filter </h4>
          <p>
            H14 HEPA provides protection against ultrafine particulate
            (PM2.5)... and pollution.
          </p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-carbon-filter" style={{ color: "#1CE4D5" }}>
            <use xlinkHref="#icon-carbon-filter" />
          </svg>
          <h4 className="icon-list__title">Carbon Filter</h4>
          <p>
            0.70 lbs of pure granular carbon for odor elimination and fighting
            off those nasty VOCs.
          </p>
        </li>
      </ul>
    </div>
  </section>
);

/**
 * @param index
 * @param setScrollToRefs
 * @param formatter
 * @param regionalPrice
 * @param localization
 * @returns {JSX.Element}
 * @constructor
 */
const OverreactorComponent = ({
  index,
  setScrollToRefs,
  formatter,
  regionalPrice,
  localization,
}) => (
  <section
    id="overreactor"
    className="filter-block"
    ref={(el) => setScrollToRefs(index, el)}
  >
    <div className="container">
      <div className="filter-block__primary">
        <figure className="filter-block__image">
          <LazyLoad>
            <FadeIn>
              <picture>
                <source srcSet={theOverreactorWebp} type="image/webp" />
                <source srcSet={theOverreactor} type="image/jpg" />
                <img src={theOverreactor} alt="the-overreactor" />
              </picture>
            </FadeIn>
          </LazyLoad>
        </figure>
        <div className="filter-block__content">
          <h2 className="filter-block__title" style={{ color: "#0A56EA" }}>
            The <LazyImg src={explodingHead} alt="exploding-head" /> <br />
            Overreactor
          </h2>
          <h3 className="filter-block__subtitle">
            Calling all Type A air breathers.
          </h3>
          <figure className="filter-block__image">
            <LazyLoad>
              <FadeIn>
                <picture>
                  <source srcSet={theOverreactorWebp} type="image/webp" />
                  <source srcSet={theOverreactor} type="image/jpg" />
                  <img src={theOverreactor} alt="the-overreactor" />
                </picture>
              </FadeIn>
            </LazyLoad>
          </figure>
          <div className="filter-block__description">
            <p>
              The mother of all filters, the Overreactor tackles just about
              everything that could be lurking in your air.
            </p>
          </div>
        </div>
        <div className="filter-block__price">
          <div className="filter-block__price-info">
            <p>
              <strong>{formatter.format(regionalPrice.price)}</strong>
              {localization.region !== SUPPORTED_REGIONS.SG && (
                <span key={generateKey("filter-block__free-info")}>
                  {" "}
                  or{" "}
                  <span
                    className="filter-block__free-sign"
                    style={{ color: " #0A56EA" }}
                  >
                    FREE
                  </span>{" "}
                  with Auto-Refill with the purchase of a Mila unit{" "}
                </span>
              )}
            </p>
          </div>
          <div className="filter-block__button-wrap">
            <Link
              to="/filters/overreactor"
              className="btn"
              style={{
                borderColor: "#0A56EA",
                background: "#0A56EA",
                color: "#ffffff",
              }}
            >
              Learn more
            </Link>
            <a
              href="https://buy.milacares.com/collections/mila-filter "
              className="btn btn--outline"
              style={{
                border: "2px solid #0A56EA",
                color: "#0A56EA",
              }}
            >
              Buy a standalone filter
            </a>
          </div>
        </div>
      </div>
      <ul className="filter-block__secondary icon-list">
        <li className="icon-list__item">
          <svg
            className="icon icon-ideal-room-size"
            style={{ color: "#0A56EA" }}
          >
            <use xlinkHref="#icon-ideal-room-size" />
          </svg>
          <h4 className="icon-list__title">Ideal Room Size</h4>
          <p>Works best in the bedroom (looks great in there, too).</p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-hepa-filter" style={{ color: "#0A56EA" }}>
            <use xlinkHref="#icon-hepa-filter" />
          </svg>
          <h4 className="icon-list__title">HEPA Filter </h4>
          <p>
            H14 HEPA filters out even the most ultrafine particle.
            <LazyImg src={flexedBiceps} alt="flexed-biceps" />
          </p>
        </li>
        <li className="icon-list__item">
          <svg className="icon icon-carbon-filter" style={{ color: "#0A56EA" }}>
            <use xlinkHref="#icon-carbon-filter" />
          </svg>
          <h4 className="icon-list__title">Carbon Filter</h4>
          <p>
            1.25 lbs of modified granular carbon specifically targeted for
            formaldehyde (ew).
          </p>
        </li>
      </ul>
    </div>
  </section>
);

/**
 * @param topBarRefRef
 * @param headerRef
 * @returns {null|JSX.Element}
 * @constructor
 */
const FilterMain = ({ topBarRef, headerRef }) => {
  const localization = useSelector(selectLocalizationData);
  const regionalPrices = useSelector(selectRegionalPricesData);
  const error = useSelector(selectRegionalPricesError);

  const BB = { price: getFilterPrice(regionalPrices, PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER) };
  const BS = { price: getFilterPrice(regionalPrices, PRODUCT_VARIANT_ABBREVIATIONS.BIG_SNEEZE) };
  const CC = { price: getFilterPrice(regionalPrices, PRODUCT_VARIANT_ABBREVIATIONS.CRITTER_CUDDLER) };
  const HW = { price: getFilterPrice(regionalPrices, PRODUCT_VARIANT_ABBREVIATIONS.HOME_WRECKER) };
  const MB = { price: getFilterPrice(regionalPrices, PRODUCT_VARIANT_ABBREVIATIONS.MAMA_TO_BE) };
  const OR = { price: getFilterPrice(regionalPrices, PRODUCT_VARIANT_ABBREVIATIONS.OVERREACTOR) };
  const RP = { price: getFilterPrice(regionalPrices, PRODUCT_VARIANT_ABBREVIATIONS.ROOKIE_PARENT) };

  const formatter = new Intl.NumberFormat(`en-${localization.region}`, {
    style: "currency",
    currency: CURRENCY_MAPPING[localization.region],
    minimumFractionDigits: 0,
  });

  const filterMainRef = useRef(null);
  const stickyScrollRef = useRef(null);
  const fillGapRef = useRef(null);
  const scrollToRefs = useRef([]).current;

  const [stickyOffsetTop, setStickyOffsetTop] = useState(0);
  const [headerNavHeight, setHeaderNavHeight] = useState(0);
  const [stickyNavHeight, setStickyNavHeight] = useState(0);
  const [windowScrollHeight, setWindowScrollHeight] = useState(0);
  const [windowWidth, setWindowWidth] = useState(0);
  const [currentSectionActive, setCurrentSectionActive] = useState(0);
  const [stickyNavOpen, setStickyNavOpen] = useState(false);
  const [stickyActive, setStickyActive] = useState(false);

  const setScrollToRefs = (index, el) => {
    scrollToRefs[index] = el;
  };

  const handleClick = (idx, event) => {
    event.preventDefault();
    const currentSectionOffset = scrollToRefs[idx].offsetTop;
    const windowHeight = window.innerHeight;
    document.scrollTop = window.scrollTo({
      top:
        currentSectionOffset +
        windowHeight -
        (headerNavHeight + stickyNavHeight),
      left: 0,
      behavior: "smooth",
    });
  };

  const handleResponsiveClick = (e) => {
    e.preventDefault();
    setStickyNavOpen(!stickyNavOpen);
  };

  useEffect(() => {
    if (windowScrollHeight > stickyOffsetTop - headerNavHeight) {
      setStickyActive(true);
      fillGapRef.current.style.paddingTop =
        windowWidth > 768 ? `${stickyNavHeight}px` : 0;
      stickyScrollRef.current.style.top = `${headerNavHeight + 10}px`;
    } else {
      setStickyActive(false);
      fillGapRef.current.style.paddingTop = `${0}px`;
      stickyScrollRef.current.style.top = 0;
    }
  }, [windowScrollHeight, stickyOffsetTop, headerNavHeight, stickyNavHeight]);

  useEffect(() => {
    scrollToRefs?.forEach((item, i) => {
      const elementScrollHeight = item.scrollHeight;
      const elementOffset = item.offsetTop;
      const headerOffsetHeight = headerNavHeight + stickyNavHeight + 100;
      const requiredOffset =
        elementScrollHeight + elementOffset + headerOffsetHeight;
      const windowHeight = window.innerHeight;
      if (
        windowScrollHeight >
          elementOffset + (windowHeight - headerOffsetHeight) &&
        requiredOffset > windowScrollHeight
      ) {
        setCurrentSectionActive(i);
      }
    });
  }, [windowScrollHeight]);

  useEffect(() => {
    const stickyHeight = stickyScrollRef?.current.clientHeight;

    const handleWindowResize = () => {
      setStickyOffsetTop(filterMainRef?.current.offsetTop);
      setHeaderNavHeight(
        topBarRef.current.clientHeight + headerRef.current.clientHeight
      );
      setStickyNavHeight(stickyHeight);
      setWindowWidth(window.innerWidth);
    };

    const handleScroll = () => {
      setWindowScrollHeight(document.documentElement.scrollTop);
      setHeaderNavHeight(
        topBarRef.current.clientHeight + headerRef.current.clientHeight
      );
    };

    document.addEventListener("DOMContentLoaded", handleWindowResize);
    window.addEventListener("resize", handleWindowResize);
    window.addEventListener("scroll", handleWindowResize);

    document.addEventListener("DOMContentLoaded", handleScroll);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("DOMContentLoaded", handleWindowResize);
      window.removeEventListener("resize", handleWindowResize);
      window.removeEventListener("scroll", handleWindowResize);

      document.removeEventListener("DOMContentLoaded", handleScroll);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return error ? null : (
    <div className="filter-main" ref={filterMainRef}>
      <span className="filter-main__fillgap" ref={fillGapRef} />
      <section
        className={`sticky-scroll sticky-scroll--fullwidth ${
          stickyNavOpen ? "sticky-scroll--is-open" : ""
        } ${stickyActive ? "sticky-scroll--fixed" : ""}`}
        ref={stickyScrollRef}
      >
        <button
          type="button"
          className={`sticky-scroll__toggle ${
            stickyNavOpen ? "sticky-scroll__toggle--is-active" : ""
          }`}
          onClick={(e) => {
            handleResponsiveClick(e);
          }}
        >
          <span className="sticky-scroll__toggle-logo">
            <img src={logo} alt="Mila" />
          </span>
          <span className="sticky-scroll__active-text">
            The {Object.values(FILTER_NAMES)[currentSectionActive]}
          </span>
          <span className="sticky-scroll__toggle-arrow">
            <svg className="icon icon-down-arrow-thin">
              <use xlinkHref="#icon-down-arrow-thin" />
            </svg>
          </span>
        </button>
        <ul
          className={`sticky-navigation ${
            stickyNavOpen ? "sticky-navigation--is-active" : ""
          }`}
        >
          {Object.values(FILTER_NAMES).map((title, i) => (
            <li
              className={`sticky-navigation__item ${
                currentSectionActive === i
                  ? "sticky-navigation__item--active"
                  : ""
              }`}
              key={title}
            >
              <button
                type="button"
                onClick={(e) => {
                  handleClick(i, e);
                  setStickyNavOpen(false);
                }}
                aria-label={`The ${title}`}
              >
                The {title}
              </button>
            </li>
          ))}
        </ul>
      </section>
      <h2 className="filter-main__title">Seven Custom Filters—One for Every Air Challenge

</h2>

      <BasicBreatherComponent
        setScrollToRefs={setScrollToRefs}
        index={0}
        formatter={formatter}
        regionalPrice={BB}
        localization={localization}
      />
      <BigSneezeComponent
        setScrollToRefs={setScrollToRefs}
        index={1}
        formatter={formatter}
        regionalPrice={BS}
        localization={localization}
      />
      <RookieParentComponent
        setScrollToRefs={setScrollToRefs}
        index={2}
        formatter={formatter}
        regionalPrice={RP}
        localization={localization}
      />
      <CritterCuddlerComponent
        setScrollToRefs={setScrollToRefs}
        index={3}
        formatter={formatter}
        regionalPrice={CC}
        localization={localization}
      />
      <HomeWreckerComponent
        setScrollToRefs={setScrollToRefs}
        index={4}
        formatter={formatter}
        regionalPrice={HW}
        localization={localization}
      />
      <MamaToBeComponent
        setScrollToRefs={setScrollToRefs}
        index={5}
        formatter={formatter}
        regionalPrice={MB}
        localization={localization}
      />
      <OverreactorComponent
        setScrollToRefs={setScrollToRefs}
        index={6}
        formatter={formatter}
        regionalPrice={OR}
        localization={localization}
      />
    </div>
  );
};

export default FilterMain;
